/**
 * RBAC V2 — Composants UI de gating frontend
 * ============================================
 *
 * Fournit :
 *  - `<PermissionDenied />` : banner d'avertissement réutilisable affichant la perm manquante
 *  - `<RequirePermission>` : guard page-level qui rend `<PermissionDenied />` si l'utilisateur
 *    n'a pas la perm requise (sinon affiche les enfants)
 *  - `<DisabledIfNoPerm>` : wrapper qui désactive et masque tooltipisé un bouton sans la perm
 *
 * Usage :
 *   <RequirePermission permission={PERMS.BACKUPS_RESTORE}>
 *     <BackupRestoreButton />
 *   </RequirePermission>
 */
import React from 'react';
import { Lock, ShieldAlert } from 'lucide-react';
import { usePermissions } from '../../contexts/PermissionsContext';
import { Button } from '../ui/button';

export const PermissionDenied = ({ permission, anyPermission, title, hint }) => {
  const required = permission || (Array.isArray(anyPermission) ? anyPermission.join(' OU ') : null);
  return (
    <div
      data-testid="permission-denied-banner"
      className="flex items-start gap-4 p-6 rounded-xl border border-amber-200 bg-amber-50 text-amber-900"
    >
      <ShieldAlert className="w-8 h-8 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-base">{title || 'Accès restreint'}</h3>
        <p className="text-sm mt-1">
          Cette section nécessite la permission&nbsp;
          {required ? (
            <code className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 font-mono text-xs">
              {required}
            </code>
          ) : (
            'requise'
          )}
          .{' '}
          {hint || 'Contactez votre administrateur si vous pensez devoir y avoir accès.'}
        </p>
      </div>
    </div>
  );
};

export const RequirePermission = ({
  permission,
  anyPermission,
  fallback,
  loadingFallback = null,
  children,
}) => {
  const { hasPermission, hasAnyPermission, loading } = usePermissions();
  if (loading) return loadingFallback;
  const allowed = anyPermission
    ? hasAnyPermission(anyPermission)
    : permission
    ? hasPermission(permission)
    : true;
  if (!allowed) {
    return fallback !== undefined ? (
      fallback
    ) : (
      <div className="p-6">
        <PermissionDenied permission={permission} anyPermission={anyPermission} />
      </div>
    );
  }
  return <>{children}</>;
};

/**
 * Wrap un bouton : désactivé + tooltip si pas de perm. Garde l'élément visible
 * pour ne pas masquer la fonctionnalité (transparence UX).
 */
export const DisabledIfNoPerm = ({ permission, children, title }) => {
  const { hasPermission, loading } = usePermissions();
  if (loading) return null;
  if (hasPermission(permission)) return <>{children}</>;
  return (
    <span
      title={title || `Permission requise : ${permission}`}
      className="inline-flex items-center opacity-50 cursor-not-allowed"
      data-testid={`disabled-no-perm-${permission}`}
    >
      <Lock className="w-3.5 h-3.5 mr-1 text-slate-400" />
      {React.cloneElement(React.Children.only(children), { disabled: true })}
    </span>
  );
};

export default RequirePermission;
